import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Settings, LogOut, Repeat } from 'lucide-react';
import { useAuthentication } from '../../../context/AuthContext';
import { useModules } from '../../../context/ModulesContext';
import { getAppContext } from '../../../utils/getAppContext';
import { getBrandConfig } from '../../../utils/brandConfig';
import { isMobileApp } from '../../../utils/isMobileApp';

/**
 * The app's burger + gear controls, reinserted into the IQ Verity dark topbar.
 * The default askSmart header is suppressed on Verity routes so its own shell
 * can own the screen; these two menus give back the app-level navigation.
 *
 * Both menus are driven from the modules catalog (useModules) + the user's
 * permissions (useAuthentication) so they stay in sync with the real header
 * and never need hand-maintained link lists.
 */
const VerityTopControls = () => {
  const { roles, canSwitchOrgs, isPlatformAdminMode, logout, entitlements } =
    useAuthentication() as any;
  const { modules } = useModules();

  const permissions: string[] = roles?.permissions ?? [];
  const [open, setOpen] = useState<null | 'burger' | 'gear'>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Same two-gate access rule the app header uses:
  //   Gate 1 — entitlement: the org must have the tool enabled (null = no gate).
  //   Gate 2 — permission: the user must hold the required permission key.
  const can = (entitlementKey: string | null, requiredPermissions: string[]): boolean => {
    if (entitlementKey != null && entitlements?.features?.[entitlementKey]?.enabled !== true) {
      return false;
    }
    return requiredPermissions.every((p) => permissions.includes(p));
  };

  const showGear =
    !isMobileApp() && permissions.some((p) => p.includes('.admin') || p.includes('.manage'));

  // A module only appears if it has the relevant permission set AND the user
  // passes both gates — mirroring the header, so only assigned modules show.
  const viewModules = modules.filter(
    (m) => m.route && m.viewPermission && can(m.entitlementKey, [m.viewPermission])
  );
  const adminModules = modules.filter(
    (m) => m.adminRoute && m.adminPermission && can(m.entitlementKey, [m.adminPermission])
  );

  const handleSwitchOrg = () => {
    const authPath = getBrandConfig(getAppContext()).authEndpoint;
    const authQuery = isMobileApp() ? '?platform=mobile' : '';
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}${authPath}${authQuery}`;
  };

  const close = () => setOpen(null);

  return (
    <div className="vr-topbar-right" ref={wrapRef}>
      {/* Gear — admin / manage tools (hidden on mobile, gated by permission) */}
      {showGear && (
        <div className="vr-menu-wrap">
          <button
            type="button"
            aria-label="Admin tools"
            className={`vr-iconbtn${open === 'gear' ? ' vr-open' : ''}`}
            onClick={() => setOpen((o) => (o === 'gear' ? null : 'gear'))}
          >
            <Settings size={17} />
          </button>
          {open === 'gear' && (
            <div className="vr-menu">
              <div className="vr-menu-label">Admin</div>
              {adminModules.length === 0 && <div className="vr-menu-empty">No admin tools available</div>}
              {adminModules.map((m) => {
                // The same naming rule the app header uses (Header.tsx) and that
                // deriveModulePermissionRows encodes: the "Manage" prefix exists only to tell an
                // admin entry apart from the user-side entry of the same module. Without it this
                // menu offered "IQ Verity" next to the burger's "IQ Verity" and the two looked like
                // the same destination. An admin-only module has nothing to be told apart from, so
                // it keeps its plain name.
                //
                // The prefix is applied to a BARE name, never stacked on one that already carries
                // it. The catalog stores "Manage Users", so the old rule produced "Manage Users"
                // sitting under a heading that already says Admin — the word twice, for a module
                // that has no user-side entry to be told apart from in the first place.
                const isAdminOnly = !m.route && !m.viewPermission;
                const bare = m.name.replace(/^Manage\s+/i, '');
                const label = isAdminOnly ? bare : `Manage ${bare}`;
                return (
                  <Link key={m.id} to={m.adminRoute as string} onClick={close}>
                    {label}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Burger — operational tools (module navigation, org, logout) */}
      <div className="vr-menu-wrap">
        <button
          type="button"
          aria-label="Menu"
          className={`vr-iconbtn${open === 'burger' ? ' vr-open' : ''}`}
          onClick={() => setOpen((o) => (o === 'burger' ? null : 'burger'))}
        >
          <Menu size={18} />
        </button>
        {open === 'burger' && (
          <div className="vr-menu">
            {!isPlatformAdminMode &&
              viewModules.map((m) => (
                <Link key={m.id} to={m.route as string} onClick={close}>
                  {m.name}
                </Link>
              ))}
            {canSwitchOrgs && (
              <button type="button" onClick={() => { close(); handleSwitchOrg(); }}>
                <Repeat size={15} /> Switch Org
              </button>
            )}
            <button type="button" className="vr-menu-danger" onClick={() => { close(); logout?.(); }}>
              <LogOut size={15} /> Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerityTopControls;
